import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Chip, IconButton, Button,
    LinearProgress, Select, MenuItem, TextField, CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useNavigate } from 'react-router-dom';

const LINE_API_URL = process.env.REACT_APP_LINE_API_URL || 'https://booking.trailernchu.co.jp/sys/line/api_v2.php';
const API_KEY = 'nc-line-sync-key-2024';
const PAGE_SIZE = 20;

interface ShadowReply {
    id: number;
    line_user_id: string;
    trigger_content: string;
    ai_reply: string;
    ai_model: string;
    ai_confidence: number;
    ai_action_type: string;
    ai_reasoning: string;
    alert_level: number;
    staff_reply_content: string | null;
    staff_replied_at: string | null;
    evaluation_score: number | null;
    created_at: string;
    line_display_name: string | null;
    custom_display_name: string | null;
    is_staff: number;
    example_reply: string | null;
}

interface Summary {
    [key: string]: { count: number; avg_confidence: number };
}

const ACTION_COLORS: Record<string, string> = {
    auto_reply: '#10b981',
    escalate: '#ef4444',
    hold: '#94a3b8',
};

const ACTION_LABELS: Record<string, string> = {
    auto_reply: 'AUTO_REPLY',
    escalate: 'ESCALATE',
    hold: 'HOLD',
};

const ALERT_COLORS = ['transparent', '#2563eb', '#f59e0b', '#ef4444'];
const ALERT_LABELS = ['なし', 'Lv1 保留', 'Lv2 料金/時間', 'Lv3 緊急'];

export default function AiChatReview() {
    const navigate = useNavigate();
    const [data, setData] = useState<ShadowReply[]>([]);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState<Summary>({});
    const [loading, setLoading] = useState(false);
    const [offset, setOffset] = useState(0);

    // Filters
    const [actionFilter, setActionFilter] = useState('');
    const [alertFilter, setAlertFilter] = useState('');
    const [userTypeFilter, setUserTypeFilter] = useState('customer');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const fetchData = useCallback(async (newOffset = 0) => {
        setLoading(true);
        const params = new URLSearchParams({
            action: 'get_shadow_replies',
            api_key: API_KEY,
            limit: String(PAGE_SIZE),
            offset: String(newOffset),
        });
        if (actionFilter) params.set('action_type', actionFilter);
        if (alertFilter !== '') params.set('alert_level', alertFilter);
        if (userTypeFilter) params.set('user_type', userTypeFilter);
        if (dateFrom) params.set('date_from', dateFrom);
        if (dateTo) params.set('date_to', dateTo);

        try {
            const res = await fetch(`${LINE_API_URL}?${params}`);
            const json = await res.json();
            if (json.success) {
                setData(json.data);
                setTotal(json.total);
                setSummary(json.summary || {});
                setOffset(newOffset);
            }
        } catch (err) {
            console.error('Failed to fetch shadow replies:', err);
        }
        setLoading(false);
    }, [actionFilter, alertFilter, userTypeFilter, dateFrom, dateTo]);

    useEffect(() => { fetchData(0); }, [fetchData]);

    const totalSummaryCount = Object.values(summary).reduce((s, v) => s + v.count, 0);

    return (
        <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 1, md: 3 } }}>
            {/* Header */}
            <Box display="flex" alignItems="center" gap={1} mb={2}>
                <IconButton onClick={() => navigate('/line')}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" fontWeight={800}>AIチャットレビュー</Typography>
                <Typography variant="body2" sx={{ color: '#64748b', ml: 1 }}>
                    {total}件
                </Typography>
            </Box>

            {/* Summary */}
            {totalSummaryCount > 0 && (
                <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {Object.entries(summary).map(([type, s]) => (
                        <Box key={type} sx={{ textAlign: 'center' }}>
                            <Chip
                                label={ACTION_LABELS[type] || type}
                                size="small"
                                sx={{ background: ACTION_COLORS[type] || '#e2e8f0', color: 'white', fontWeight: 800, mb: 0.5 }}
                            />
                            <Typography variant="body2" fontWeight={700}>{s.count}件</Typography>
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                信頼度 {(s.avg_confidence * 100).toFixed(0)}%
                            </Typography>
                        </Box>
                    ))}
                </Paper>
            )}

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Select
                    size="small"
                    value={actionFilter}
                    onChange={(e) => setActionFilter(e.target.value)}
                    displayEmpty
                    sx={{ minWidth: 140 }}
                >
                    <MenuItem value="">全アクション</MenuItem>
                    <MenuItem value="auto_reply">AUTO_REPLY</MenuItem>
                    <MenuItem value="escalate">ESCALATE</MenuItem>
                    <MenuItem value="hold">HOLD</MenuItem>
                </Select>
                <Select
                    size="small"
                    value={alertFilter}
                    onChange={(e) => setAlertFilter(e.target.value)}
                    displayEmpty
                    sx={{ minWidth: 140 }}
                >
                    <MenuItem value="">全レベル</MenuItem>
                    <MenuItem value="0">Lv0 通常</MenuItem>
                    <MenuItem value="1">Lv1 保留</MenuItem>
                    <MenuItem value="2">Lv2 料金/時間</MenuItem>
                    <MenuItem value="3">Lv3 緊急</MenuItem>
                </Select>
                <Select
                    size="small"
                    value={userTypeFilter}
                    onChange={(e) => setUserTypeFilter(e.target.value)}
                    displayEmpty
                    sx={{ minWidth: 130 }}
                >
                    <MenuItem value="">全ユーザー</MenuItem>
                    <MenuItem value="customer">一般客のみ</MenuItem>
                    <MenuItem value="staff">スタッフのみ</MenuItem>
                </Select>
                <TextField size="small" type="date" label="From" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
                <TextField size="small" type="date" label="To" value={dateTo} onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
            </Paper>

            {/* Loading */}
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {/* Cards */}
            <Box display="flex" flexDirection="column" gap={2}>
                {data.map((row) => (
                    <Paper key={row.id} sx={{ p: 2, border: '1px solid #e2e8f0' }}>
                        {/* Card header */}
                        <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                {new Date(row.created_at.replace(/-/g, '/')).toLocaleString('ja-JP')}
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                                {row.custom_display_name || row.line_display_name || row.line_user_id}
                            </Typography>
                            {Number(row.is_staff) === 1 && (
                                <Chip label="スタッフ" size="small" sx={{ background: '#7c3aed', color: 'white', fontWeight: 800, fontSize: '10px', height: 22 }} />
                            )}
                            <Chip
                                label={ACTION_LABELS[row.ai_action_type] || row.ai_action_type}
                                size="small"
                                sx={{
                                    background: ACTION_COLORS[row.ai_action_type] || '#e2e8f0',
                                    color: 'white',
                                    fontWeight: 800,
                                    fontSize: '10px',
                                    height: 22,
                                }}
                            />
                            {row.alert_level > 0 && (
                                <Chip
                                    label={ALERT_LABELS[row.alert_level]}
                                    size="small"
                                    sx={{
                                        background: ALERT_COLORS[row.alert_level],
                                        color: 'white',
                                        fontWeight: 800,
                                        fontSize: '10px',
                                        height: 22,
                                    }}
                                />
                            )}
                            <Box display="flex" alignItems="center" gap={0.5} ml="auto">
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
                                    信頼度
                                </Typography>
                                <Box sx={{ width: 60 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={Number(row.ai_confidence) * 100}
                                        sx={{
                                            height: 6,
                                            borderRadius: 3,
                                            bgcolor: '#e2e8f0',
                                            '& .MuiLinearProgress-bar': {
                                                bgcolor: Number(row.ai_confidence) >= 0.8 ? '#10b981' : Number(row.ai_confidence) >= 0.5 ? '#f59e0b' : '#ef4444',
                                            },
                                        }}
                                    />
                                </Box>
                                <Typography variant="caption" fontWeight={700}>
                                    {(Number(row.ai_confidence) * 100).toFixed(0)}%
                                </Typography>
                            </Box>
                        </Box>

                        {/* Customer message */}
                        <Box sx={{ p: 1.5, mb: 1, background: '#f1f5f9', borderRadius: 2, borderLeft: '3px solid #3b82f6' }}>
                            <Typography variant="caption" sx={{ color: '#3b82f6', fontWeight: 700 }}>顧客メッセージ</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{row.trigger_content}</Typography>
                        </Box>

                        {/* AI reply */}
                        <Box sx={{ p: 1.5, mb: 1, background: '#faf5ff', borderRadius: 2, borderLeft: `3px solid ${ACTION_COLORS[row.ai_action_type] || '#94a3b8'}` }}>
                            <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 700 }}>AI返信候補</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{row.ai_reply}</Typography>
                        </Box>

                        {/* Staff reply (if exists) */}
                        {row.staff_reply_content && (
                            <Box sx={{ p: 1.5, mb: 1, background: '#f0fdf4', borderRadius: 2, borderLeft: '3px solid #10b981' }}>
                                <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 700 }}>
                                    スタッフ実返信 ({row.staff_replied_at ? new Date(row.staff_replied_at.replace(/-/g, '/')).toLocaleString('ja-JP') : ''})
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{row.staff_reply_content}</Typography>
                            </Box>
                        )}

                        {/* Reasoning */}
                        {row.ai_reasoning && (
                            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.5 }}>
                                理由: {row.ai_reasoning}
                            </Typography>
                        )}

                        {/* 返信文例 */}
                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #e2e8f0' }}>
                            <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 700, display: 'block', mb: 0.5 }}>
                                ✏️ 返信文例
                            </Typography>
                            <TextField
                                size="small"
                                multiline
                                minRows={1}
                                maxRows={4}
                                fullWidth
                                placeholder="模範返信を記入..."
                                defaultValue={row.example_reply || ''}
                                onBlur={async (e) => {
                                    const val = e.target.value.trim();
                                    if (val === (row.example_reply || '')) return;
                                    try {
                                        await fetch(`${LINE_API_URL}?action=update_shadow_reply&api_key=${API_KEY}`, {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ id: row.id, example_reply: val }),
                                        });
                                        row.example_reply = val;
                                    } catch (err) {
                                        console.error('Failed to save example reply:', err);
                                    }
                                }}
                                sx={{
                                    '& .MuiInputBase-root': { fontSize: '13px' },
                                    '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                                }}
                            />
                        </Box>
                    </Paper>
                ))}

                {!loading && data.length === 0 && (
                    <Typography variant="body2" sx={{ color: '#94a3b8', textAlign: 'center', py: 4 }}>
                        データがありません
                    </Typography>
                )}
            </Box>

            {/* Pagination */}
            {total > PAGE_SIZE && (
                <Box display="flex" justifyContent="center" alignItems="center" gap={2} mt={3}>
                    <Button
                        size="small"
                        startIcon={<NavigateBeforeIcon />}
                        disabled={offset === 0}
                        onClick={() => fetchData(Math.max(0, offset - PAGE_SIZE))}
                    >
                        前へ
                    </Button>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                        {offset + 1} - {Math.min(offset + PAGE_SIZE, total)} / {total}
                    </Typography>
                    <Button
                        size="small"
                        endIcon={<NavigateNextIcon />}
                        disabled={offset + PAGE_SIZE >= total}
                        onClick={() => fetchData(offset + PAGE_SIZE)}
                    >
                        次へ
                    </Button>
                </Box>
            )}
        </Box>
    );
}
